import { useMemo } from 'react';
import type { ReactNode, ComponentPropsWithoutRef } from 'react';
import Markdown from 'react-markdown';
import type { ChatMessage as ChatMessageType } from '../../hooks/usePolicyChat';
import type { PolicyPack } from '../../api/client';

const effectBadgeColors: Record<string, string> = {
  ALLOW: 'bg-[rgba(52,211,153,0.12)] text-[var(--color-success)]',
  DENY: 'bg-[rgba(248,113,113,0.12)] text-[var(--color-error)]',
  TRANSFORM: 'bg-[rgba(251,191,36,0.12)] text-[var(--color-warning)]',
  REQUIRE_APPROVAL: 'bg-[rgba(139,92,246,0.12)] text-accent-violet',
};

const effectIcons: Record<string, string> = {
  ALLOW: '\u2713',
  DENY: '\u2715',
  REQUIRE_APPROVAL: '\u23F3',
  TRANSFORM: '\u27F3',
};

const EFFECT_KEYWORDS = ['ALLOW', 'DENY', 'REQUIRE_APPROVAL', 'TRANSFORM'];
const EFFECT_RE = new RegExp(`\\b(${EFFECT_KEYWORDS.join('|')})\\b`, 'g');

function PolicySummaryCard({ policy }: { policy: PolicyPack }) {
  return (
    <div className="my-3 rounded-lg border border-border-dark bg-surface-2 overflow-hidden">
      <div className="px-4 py-3 border-b border-border-dark flex items-center justify-between">
        <span className="text-xs font-medium text-text-secondary">Policy Proposal</span>
        <span className="text-xs text-text-tertiary">v{policy.version || '1.0.0'}</span>
      </div>
      <div className="px-4 py-3 space-y-3">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[rgba(139,92,246,0.1)]">
            <svg className="h-4 w-4 text-accent-violet" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-text-primary">{policy.name}</p>
            {policy.description && (
              <p className="mt-0.5 text-xs text-text-secondary leading-relaxed">{policy.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs text-text-secondary">
          <span>{(policy.rules || []).length} rule{(policy.rules || []).length !== 1 ? 's' : ''}</span>
          <span>Default: <span className={`font-medium ${effectBadgeColors[policy.default_effect || 'DENY']?.split(' ')[1] || 'text-text-primary'}`}>{policy.default_effect || 'DENY'}</span></span>
        </div>
        {(policy.rules || []).length > 0 && (
          <div className="space-y-1.5">
            {policy.rules.map((rule, i) => (
              <div key={i} className="flex items-center gap-2 rounded-md bg-surface-1 px-3 py-2">
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded text-[10px] font-bold ${effectBadgeColors[rule.effect] || 'bg-surface-2 text-text-primary'}`}>
                  {effectIcons[rule.effect] || '?'}
                </span>
                <div className="min-w-0 flex-1">
                  <span className="text-xs font-medium text-text-primary">{rule.name}</span>
                  {rule.description && (
                    <p className="text-[11px] text-text-tertiary truncate">{rule.description}</p>
                  )}
                </div>
                <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${effectBadgeColors[rule.effect] || ''}`}>
                  {rule.effect}
                </span>
              </div>
            ))}
          </div>
        )}
        {policy.domain_allowlist && policy.domain_allowlist.length > 0 && (
          <div className="text-xs text-text-secondary">
            <span className="font-medium">Allowlist:</span> {policy.domain_allowlist.join(', ')}
          </div>
        )}
        {policy.domain_blocklist && policy.domain_blocklist.length > 0 && (
          <div className="text-xs text-text-secondary">
            <span className="font-medium">Blocklist:</span> {policy.domain_blocklist.join(', ')}
          </div>
        )}
      </div>
    </div>
  );
}

/** Insert inline effect badges (ALLOW, DENY, etc.) into text nodes. */
function withEffectBadges(text: string): ReactNode {
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const re = new RegExp(EFFECT_RE.source, 'g');

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const kw = match[1];
    parts.push(
      <span key={match.index} className={`inline-block rounded-full px-1.5 py-0.5 text-xs font-semibold ${effectBadgeColors[kw] || ''}`}>
        {kw}
      </span>,
    );
    lastIndex = re.lastIndex;
  }

  if (lastIndex === 0) return text;
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return <>{parts}</>;
}

/** Custom markdown components with Tailwind styling + effect badges. */
const mdComponents = {
  p: ({ children, ...props }: ComponentPropsWithoutRef<'p'>) => (
    <p className="text-sm leading-relaxed my-1.5" {...props}>
      {processChildren(children)}
    </p>
  ),
  h1: ({ children, ...props }: ComponentPropsWithoutRef<'h1'>) => (
    <h1 className="text-base font-bold text-text-primary mt-3 mb-1" {...props}>{children}</h1>
  ),
  h2: ({ children, ...props }: ComponentPropsWithoutRef<'h2'>) => (
    <h2 className="text-sm font-bold text-text-primary mt-3 mb-1" {...props}>{children}</h2>
  ),
  h3: ({ children, ...props }: ComponentPropsWithoutRef<'h3'>) => (
    <h3 className="text-sm font-semibold text-text-primary mt-2 mb-1" {...props}>{children}</h3>
  ),
  strong: ({ children, ...props }: ComponentPropsWithoutRef<'strong'>) => (
    <strong className="font-semibold" {...props}>{children}</strong>
  ),
  em: ({ children, ...props }: ComponentPropsWithoutRef<'em'>) => (
    <em className="italic" {...props}>{children}</em>
  ),
  ul: ({ children, ...props }: ComponentPropsWithoutRef<'ul'>) => (
    <ul className="my-1.5 space-y-0.5 list-disc ml-4" {...props}>{children}</ul>
  ),
  ol: ({ children, ...props }: ComponentPropsWithoutRef<'ol'>) => (
    <ol className="my-1.5 space-y-0.5 list-decimal ml-4" {...props}>{children}</ol>
  ),
  li: ({ children, ...props }: ComponentPropsWithoutRef<'li'>) => (
    <li className="text-sm" {...props}>{processChildren(children)}</li>
  ),
  code: ({ children, className, ...props }: ComponentPropsWithoutRef<'code'> & { className?: string }) => {
    // Fenced code block has className like "language-json"
    if (className) {
      return (
        <code className={`block overflow-x-auto rounded-lg bg-surface-2 p-3 text-xs font-mono text-text-primary ${className}`} {...props}>
          {children}
        </code>
      );
    }
    // Inline code
    return (
      <code className="rounded bg-surface-2 px-1.5 py-0.5 text-xs font-mono text-accent-violet" {...props}>
        {children}
      </code>
    );
  },
  pre: ({ children, ...props }: ComponentPropsWithoutRef<'pre'>) => (
    <pre className="my-2 overflow-x-auto rounded-lg bg-surface-2 text-xs" {...props}>{children}</pre>
  ),
  hr: () => <hr className="my-3 border-border-dark" />,
  a: ({ children, href, ...props }: ComponentPropsWithoutRef<'a'>) => (
    <a href={href} className="text-accent-violet underline hover:opacity-80" target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  ),
  blockquote: ({ children, ...props }: ComponentPropsWithoutRef<'blockquote'>) => (
    <blockquote className="my-2 border-l-2 border-accent-violet pl-3 text-sm text-text-secondary italic" {...props}>
      {children}
    </blockquote>
  ),
};

/** Process children to inject effect badges into text nodes. */
function processChildren(children: ReactNode): ReactNode {
  if (typeof children === 'string') return withEffectBadges(children);
  if (Array.isArray(children)) return children.map((c, i) => (
    typeof c === 'string' ? <span key={i}>{withEffectBadges(c)}</span> : c
  ));
  return children;
}

function PolicyLoadingCard() {
  return (
    <div className="my-3 rounded-lg border border-border-dark bg-surface-2 overflow-hidden animate-pulse">
      <div className="px-4 py-3 border-b border-border-dark flex items-center justify-between">
        <span className="text-xs font-medium text-text-secondary">Generating policy...</span>
      </div>
      <div className="px-4 py-3 space-y-3">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 h-8 w-8 shrink-0 rounded-lg bg-surface-1" />
          <div className="flex-1 space-y-2">
            <div className="h-3.5 w-32 rounded bg-surface-1" />
            <div className="h-3 w-48 rounded bg-surface-1" />
          </div>
        </div>
        <div className="flex gap-4">
          <div className="h-3 w-16 rounded bg-surface-1" />
          <div className="h-3 w-24 rounded bg-surface-1" />
        </div>
        <div className="space-y-1.5">
          <div className="h-9 rounded-md bg-surface-1" />
          <div className="h-9 rounded-md bg-surface-1" />
        </div>
      </div>
    </div>
  );
}

/** Strip <policy> blocks and return [cleanMarkdown, parsedPolicies, hasPartialPolicy]. */
function extractPolicies(content: string, isStreaming: boolean): [string, PolicyPack[], boolean] {
  const policies: PolicyPack[] = [];
  let hasPartialPolicy = false;

  // Replace complete <policy>...</policy> blocks
  let cleaned = content.replace(/<policy>([\s\S]*?)<\/policy>/g, (_m, json) => {
    try { policies.push(JSON.parse(json.trim())); } catch { /* skip */ }
    return '\n\n---POLICY_CARD---\n\n';
  });

  if (isStreaming) {
    // Detect unclosed <policy> tag (opened but </policy> not yet received)
    if (cleaned.includes('<policy>')) {
      cleaned = cleaned.replace(/<policy>[\s\S]*$/, '\n\n---POLICY_LOADING---\n\n');
      hasPartialPolicy = true;
    }

    // Strip partial <policy tag building up at end of stream:
    // matches trailing <, <p, <po, <pol, <poli, <polic, <policy (without >)
    if (!hasPartialPolicy) {
      const partialTag = cleaned.match(/<(?:p(?:o(?:l(?:i(?:c(?:y)?)?)?)?)?)?$/);
      if (partialTag) {
        cleaned = cleaned.slice(0, partialTag.index);
        hasPartialPolicy = true;
      }
    }

    // Also strip trailing "---" or "---\n" that precedes the <policy> tag
    if (hasPartialPolicy) {
      cleaned = cleaned.replace(/\n---\s*$/, '');
    }
  }

  // Strip "---" separator before placeholders
  cleaned = cleaned.replace(/\n---\n\s*\n?---POLICY_CARD---/g, '\n\n---POLICY_CARD---');
  cleaned = cleaned.replace(/\n---\n\s*\n?---POLICY_LOADING---/g, '\n\n---POLICY_LOADING---');
  return [cleaned, policies, hasPartialPolicy];
}

export interface ChatMessageProps {
  message: ChatMessageType;
  isStreaming?: boolean;
}

export function ChatMessage({ message, isStreaming }: ChatMessageProps) {
  const isUser = message.role === 'user';
  const streaming = isStreaming && !isUser;

  const [markdownParts, policies, hasPartialPolicy] = useMemo(
    () => extractPolicies(message.content, !!streaming),
    [message.content, streaming],
  );

  // Split on both POLICY_CARD and POLICY_LOADING placeholders
  const segments = markdownParts.split(/---POLICY_CARD---|---POLICY_LOADING---/);
  // Build ordered list of placeholder types
  const placeholders: ('card' | 'loading')[] = [];
  const placeholderRe = /---POLICY_(CARD|LOADING)---/g;
  let m: RegExpExecArray | null;
  while ((m = placeholderRe.exec(markdownParts)) !== null) {
    placeholders.push(m[1] === 'CARD' ? 'card' : 'loading');
  }

  let policyIdx = 0;

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-xl px-4 py-3 ${
          isUser
            ? 'bg-[rgba(139,92,246,0.1)] text-text-primary'
            : 'border border-border-dark bg-surface-1 text-text-primary'
        }`}
      >
        {segments.map((segment, i) => {
          const placeholder = placeholders[i];
          let card: ReactNode = null;
          if (placeholder === 'card' && policyIdx < policies.length) {
            card = <PolicySummaryCard key={`card-${i}`} policy={policies[policyIdx]} />;
            policyIdx++;
          } else if (placeholder === 'loading') {
            card = <PolicyLoadingCard key={`loading-${i}`} />;
          }
          return (
            <span key={i}>
              {segment.trim() && (
                <Markdown components={mdComponents}>{segment}</Markdown>
              )}
              {card}
            </span>
          );
        })}
        {streaming && hasPartialPolicy && !placeholders.includes('loading') && (
          <PolicyLoadingCard />
        )}
        {streaming && !hasPartialPolicy && (
          <span className="inline-flex gap-1 mt-1">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-violet animate-pulse" />
            <span className="h-1.5 w-1.5 rounded-full bg-accent-violet animate-pulse [animation-delay:150ms]" />
            <span className="h-1.5 w-1.5 rounded-full bg-accent-violet animate-pulse [animation-delay:300ms]" />
          </span>
        )}
      </div>
    </div>
  );
}
